import type { KeyboardEvent, MouseEvent } from "react";
import type { BaseCardProps, CardVariant } from "../../types/cards.types";

const commonStyles = {
	base: "rounded-lg border bg-white p-4 text-left transition-all",
	selected: "border-brand-500 shadow-sm",
	default: "border-gray-200 hover:border-brand-300 hover:shadow-sm",
};

const vehicleStyles = {
	base: "rounded-xl border-2 bg-white p-4 text-left",
	selected: "border-blue-500 shadow-lg",
	default: "border-slate-200 hover:border-indigo-300",
};

const getContainerClassName = (variant: CardVariant, isSelected: boolean) => {
	const styles = variant === "vehicle" ? vehicleStyles : commonStyles;
	const stateClass = isSelected ? styles.selected : styles.default;
	return `${styles.base} ${stateClass}`;
};

const handleKeyDown = (e: KeyboardEvent, onClick?: () => void) => {
	if (e.key === "Enter" || e.key === " ") {
		e.preventDefault();
		onClick?.();
	}
};

const handleClick = (
	e: MouseEvent<HTMLButtonElement>,
	onClick?: () => void
) => {
	e.stopPropagation();
	onClick?.();
};

export const BaseCard = ({
	children,
	isSelected = false,
	onClick,
	style,
	variant,
	className = "",
	role,
	tabIndex,
}: BaseCardProps) => {
	const classNameBase = getContainerClassName(variant, isSelected);

	if (!onClick) {
		return (
			<div
				className={`${classNameBase} ${className}`}
				role={role}
				style={style}
				tabIndex={tabIndex}
			>
				{children}
			</div>
		);
	}

	return (
		<button
			className={`${classNameBase} ${className}`}
			onClick={(e) => handleClick(e, onClick)}
			onKeyDown={(e) => handleKeyDown(e, onClick)}
			role={role}
			style={style}
			tabIndex={tabIndex}
			type="button"
		>
			{children}
		</button>
	);
};
